import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, CheckCircle, Info, Loader, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useI18n } from '../i18n/useI18n'
import { useModal } from './useModal'

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

const variantStyles = {
  success: {
    iconBg: 'bg-success-500/20',
    iconColor: 'text-success-500',
    confirmBg: 'bg-success-500 enabled:hover:bg-green-600 focus:ring-green-500',
  },
  error: {
    iconBg: 'bg-error-500/20',
    iconColor: 'text-error-500',
    confirmBg: 'bg-error-500 enabled:hover:bg-red-600 focus:ring-red-500',
  },
  warning: {
    iconBg: 'bg-warning-500/20',
    iconColor: 'text-warning-500',
    confirmBg: 'bg-warning-500 enabled:hover:bg-warning-600 focus:ring-warning-500',
  },
  info: {
    iconBg: 'bg-info-500/20',
    iconColor: 'text-info-500',
    confirmBg: 'bg-info-500 enabled:hover:bg-blue-600 focus:ring-blue-500',
  },
}

export const Modal = () => {
  const { modal, hideModal } = useModal()
  const { t } = useI18n()
  const [isModalLoading, setIsModalLoading] = useState(false)
  if (!modal) return null

  const IconComponent = iconMap[modal.type]
  const styles = variantStyles[modal.type]

  const handleConfirm = async () => {
    if (modal.onConfirm) {
      setIsModalLoading(true)
      await modal.onConfirm()
    }
    setIsModalLoading(false)
    hideModal()
  }

  const handleCancel = () => {
    if (modal.onCancel) {
      modal.onCancel()
    }
    hideModal()
  }

  const handleClose = () => {
    if (!modal.closeOnOverlayClick) return
    if (modal.onClose) {
      modal.onClose()
    }
    hideModal()
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-background-overlay/80 flex items-center justify-center z-[9999]"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-surface-elevated rounded-xl shadow-xl max-w-sm w-full mx-4 p-4"
        >
          <div className="flex items-center mb-4">
            <div className={`size-10 flex-shrink-0 ${styles.iconBg} rounded-xl flex items-center justify-center mr-3`}>
              <IconComponent className={`w-5 h-5 ${styles.iconColor}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">{modal.title}</h3>
              {modal.message && <p className="text-sm text-text-secondary">{modal.message}</p>}
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            {modal.showCancel && (
              <button
                onClick={handleCancel}
                disabled={isModalLoading}
                className="px-4 py-2 text-sm font-medium text-text-secondary enabled:hover:text-text-primary transition-colors rounded-lg enabled:hover:bg-surface-hover"
              >
                {modal.cancelText || t('dialog.cancel')}
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={isModalLoading}
              className={`px-4 py-2 min-w-[100px] text-white font-medium text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-elevated ${styles.confirmBg}`}
            >
              {isModalLoading ? (
                <Loader className="animate-spin size-4 mx-auto" />
              ) : (
                modal.confirmText || t('dialog.confirm')
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
